var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx } from "react/jsx-runtime";
import * as profileApi from '@marketing/api/profile';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatches, useParams } from 'react-router';
import { INITIAL_DOG_STATE, INITIAL_PET_SURVEY_STATE } from './enums';
import { trackCreateAccount } from './trackers';
import { determineProfileProgressPercentage, findFreeGiftWithPurchaseItem, findProductTypeFromHashString, getProfileBasePath, isValidPet, } from './utils';
import { sendGoogleTagManagerEvent, trackInteractionEvent } from '@trackers';
import { isOutOfStockRecipe, makeDeepCopy, petRecipeMixIncludesOOSRecipe } from '@utils/common_utils';
import { FUNNEL_VERSIONS_PRIORITY_MAP, OUT_OF_STOCK_PRODUCT_TYPES_PROFILE, RECIPE_MODE, } from '@variables';
var DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';
export var ProfileContext = createContext(undefined);
export var ProfileProvider = function (_a) {
    var children = _a.children, initialData = _a.initialData;
    /**
     * Since we have profileLoader (parent) and routeLoader (child), find the routeLoader that gives
     * current step data, which in the hierarchy is the last match.
     */
    var matches = useMatches();
    var currentMatch = matches[matches.length - 1];
    var currentStep = currentMatch.data;
    var location = useLocation();
    var petIndex = useParams().petIndex;
    var _b = useState(initialData), profileData = _b[0], setProfileData = _b[1];
    var _c = useState(false), serverLoading = _c[0], setServerLoading = _c[1];
    var _d = useState(petIndex ? parseInt(petIndex) : profileData.pets.length - 1 || 0), currentPetIndex = _d[0], setCurrentPetIndex = _d[1];
    var _e = useState(profileData.pets[currentPetIndex]), currentPet = _e[0], setCurrentPet = _e[1];
    var _f = useState(profileData.petSurveys[currentPetIndex]), currentPetSurvey = _f[0], setCurrentPetSurvey = _f[1];
    var _g = useState(null), freeGiftWithPurchase = _g[0], setFreeGiftWithPurchase = _g[1];
    var _h = useState(null), facebookSubscribeEventProps = _h[0], setFacebookSubscribeEventProps = _h[1];
    var _j = useState([]), serverErrors = _j[0], setServerErrors = _j[1];
    var addOnProductFromHash = useMemo(function () {
        var productTypeFromHash = document.location.hash.slice(1);
        return findProductTypeFromHashString(profileData.addOnProducts, productTypeFromHash);
    }, [document.location.hash]);
    useEffect(function () {
        var shouldSaveProfile = false;
        // If any pet has an OOS recipe, wipe related fields and save
        var petsCopy = profileData.pets.map(function (pet) {
            if (isOutOfStockRecipe(pet.recipe, pet.gramsBucket) ||
                petRecipeMixIncludesOOSRecipe(pet.recipeMix, pet.gramsBucket)) {
                shouldSaveProfile = true;
                return __assign(__assign({}, pet), { recipeMode: RECIPE_MODE.FIXED, recipe: null, recipeMix: [], recipeRotation: [], mealPlanItems: [] });
            }
            else {
                return pet;
            }
        });
        // Add addon product from hash to orderItems if it exists
        // But do not increase quantity if it already exists in cart
        var orderItemsCopy = makeDeepCopy(profileData.orderItems);
        if (addOnProductFromHash &&
            !OUT_OF_STOCK_PRODUCT_TYPES_PROFILE.includes(addOnProductFromHash.key.name)) {
            shouldSaveProfile = true;
            var orderItemCopy = orderItemsCopy.find(function (item) { return item.productType === addOnProductFromHash.key.name; });
            // Add one unit if it isn't already added
            if (!orderItemCopy) {
                orderItemsCopy.push({
                    productType: addOnProductFromHash.key.name,
                    units: 1,
                    isRecurring: false,
                });
            }
            else if (orderItemCopy.units === 0) {
                // Add one unit if it was previously 0
                var orderItemCopyIndex = orderItemsCopy.findIndex(function (item) { return item.productType === addOnProductFromHash.key.name; });
                orderItemsCopy[orderItemCopyIndex] = __assign(__assign({}, orderItemCopy), { units: orderItemCopy.units + 1 });
            }
        }
        var inStockOrderItems = orderItemsCopy.filter(function (item) { return !OUT_OF_STOCK_PRODUCT_TYPES_PROFILE.includes(item.productType); });
        if (inStockOrderItems.length !== orderItemsCopy.length) {
            shouldSaveProfile = true;
        }
        if (shouldSaveProfile) {
            saveProfile({
                pets: petsCopy,
                orderItems: inStockOrderItems,
            });
        }
    }, []);
    // Find any free gift with purchase in orderItems and save to state
    useEffect(function () {
        var freeGiftWithPurchaseItem = findFreeGiftWithPurchaseItem(profileData.orderItems);
        if (freeGiftWithPurchaseItem) {
            setFreeGiftWithPurchase(freeGiftWithPurchaseItem);
        }
    }, []);
    useEffect(function () {
        setServerErrors([]);
        if (currentStep.GTMViewEvent) {
            sendFunnelGTMEvent(currentStep.GTMViewEvent);
        }
    }, [location.pathname]);
    var updateCurrentPet = function (updates) {
        setCurrentPet(function (prevPet) { return (__assign(__assign({}, prevPet), updates)); });
    };
    var updateCurrentPetSurvey = function (updates) {
        setCurrentPetSurvey(function (prevPetSurvey) { return (__assign(__assign({}, prevPetSurvey), updates)); });
    };
    var addPet = function () {
        var pets = profileData.pets, petSurveys = profileData.petSurveys;
        var newPet = INITIAL_DOG_STATE;
        var petsCopy = makeDeepCopy(pets);
        var petSurveysCopy = makeDeepCopy(petSurveys);
        petsCopy.push(newPet);
        petSurveysCopy.push(INITIAL_PET_SURVEY_STATE);
        setCurrentPetIndex(petsCopy.length - 1);
        setCurrentPet(newPet);
        setCurrentPetSurvey(petSurveysCopy[petSurveysCopy.length - 1]);
        return newPet;
    };
    var removePet = function (petIndex) { return __awaiter(void 0, void 0, void 0, function () {
        var petsCopy, petSurveysCopy;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    petsCopy = profileData.pets.filter(function (_, index) { return petIndex !== index; });
                    petSurveysCopy = profileData.petSurveys.filter(function (_, index) { return petIndex !== index; });
                    setCurrentPet(null);
                    setCurrentPetIndex(null);
                    return [4 /*yield*/, saveProfile({ pets: petsCopy, petSurveys: petSurveysCopy })];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    }); };
    /**
     * Save profile with the current pet's updates
     */
    var savePetAndProfile = function (profilePayloadUpdates) { return __awaiter(void 0, void 0, void 0, function () {
        var petsCopy;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    petsCopy = profileData.pets.map(function (pet, index) {
                        if (index === currentPetIndex) {
                            return __assign(__assign({}, pet), currentPet);
                        }
                        else {
                            return pet;
                        }
                    });
                    return [4 /*yield*/, saveProfile(__assign({ pets: petsCopy }, profilePayloadUpdates))];
                case 1: return [2 /*return*/, _a.sent()];
            }
        });
    }); };
    var saveProfile = function (profilePayloadUpdates) { return __awaiter(void 0, void 0, void 0, function () {
        var data, response, error_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    setServerLoading(true);
                    data = __assign({ uuid: profileData.uuid, pets: profileData.pets.filter(isValidPet), petSurveys: profileData.petSurveys.filter(function (_, index) {
                            isValidPet(profileData.pets[index]);
                        }), requestTimestamp: new Date().getTime(), smsOptIn: profileData.smsOptIn || false }, profilePayloadUpdates);
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, profileApi.saveProfile(data)];
                case 2:
                    response = _c.sent();
                    setProfileData(response.data);
                    setServerErrors([]);
                    return [2 /*return*/, response.data];
                case 3:
                    error_1 = _c.sent();
                    if ((_b = (_a = error_1 === null || error_1 === void 0 ? void 0 : error_1.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) {
                        setServerErrors(error_1.response.data.errors || [DEFAULT_ERROR_MESSAGE]);
                    }
                    else {
                        setServerErrors([DEFAULT_ERROR_MESSAGE]);
                    }
                    console.error('Error saving profile:', error_1);
                    return [3 /*break*/, 5];
                case 4:
                    setServerLoading(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    var signup = function (stripeToken, failureCallback) { return __awaiter(void 0, void 0, void 0, function () {
        var data, response, error_2;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    setServerLoading(true);
                    data = {
                        uuid: profileData.uuid,
                        stripeToken: stripeToken,
                        promoCode: profileData.promoCode,
                        facebookSubscribeProps: facebookSubscribeEventProps,
                        ccpaOptOut: window.IS_CCPA_OPTED_OUT,
                    };
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, profileApi.signup(data)];
                case 2:
                    response = _c.sent();
                    trackCreateAccount(profileData);
                    localStorage.setItem('calledTrackers', 'false');
                    window.setTimeout(function () { return window.location.replace('/account/onboarding/'); }, 300);
                    setServerErrors([]);
                    return [2 /*return*/, response.data];
                case 3:
                    error_2 = _c.sent();
                    setServerLoading(false);
                    if ((_b = (_a = error_2 === null || error_2 === void 0 ? void 0 : error_2.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.errors) {
                        setServerErrors(error_2.response.data.errors || [DEFAULT_ERROR_MESSAGE]);
                    }
                    else {
                        setServerErrors([DEFAULT_ERROR_MESSAGE]);
                    }
                    console.error('Error signing up:', error_2);
                    failureCallback();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    // TODO (geraldine): Eventually I think we intend to allow IEs without customer/profile keyUrlsafes?
    var trackProfileInteractionEvent = function (eventName, experimentName) {
        if (experimentName === void 0) { experimentName = null; }
        if (profileData.profileKeyUrlsafe) {
            return trackInteractionEvent(eventName, experimentName, null, profileData.profileKeyUrlsafe);
        }
    };
    var sendFunnelGTMEvent = function (eventName, additionalData) {
        if (additionalData === void 0) { additionalData = {}; }
        // Attach funnel status for Start page and new priority name for version for other pages
        var funnelStatus = profileData.pets.length ? 'cookied' : 'static';
        var funnelPriority = FUNNEL_VERSIONS_PRIORITY_MAP[profileData.version];
        var GTMEvent = eventName.replace('VERSION', funnelPriority).replace('STATUS', funnelStatus);
        // UUID passed with every event for tracking purposes
        sendGoogleTagManagerEvent(__assign({ event: GTMEvent, uuid: profileData.uuid }, additionalData));
    };
    var profileProgressPercentage = determineProfileProgressPercentage(currentStep.progressPercentage, profileData);
    var profileBasePath = getProfileBasePath(profileData.version);
    return (_jsx(ProfileContext.Provider, __assign({ value: {
            addOnProductFromHash: addOnProductFromHash,
            addPet: addPet,
            currentPet: currentPet,
            currentPetIndex: currentPetIndex,
            currentPetSurvey: currentPetSurvey,
            currentStep: currentStep,
            freeGiftWithPurchase: freeGiftWithPurchase,
            profileBasePath: profileBasePath,
            profileData: profileData,
            profileProgressPercentage: profileProgressPercentage,
            removePet: removePet,
            savePetAndProfile: savePetAndProfile,
            saveProfile: saveProfile,
            signup: signup,
            sendFunnelGTMEvent: sendFunnelGTMEvent,
            serverErrors: serverErrors,
            serverLoading: serverLoading,
            setCurrentPetIndex: setCurrentPetIndex,
            setCurrentPet: setCurrentPet,
            setCurrentPetSurvey: setCurrentPetSurvey,
            setFacebookSubscribeEventProps: setFacebookSubscribeEventProps,
            trackProfileInteractionEvent: trackProfileInteractionEvent,
            updateCurrentPet: updateCurrentPet,
            updateCurrentPetSurvey: updateCurrentPetSurvey,
        } }, { children: children })));
};
export var useProfileContext = function () {
    var context = useContext(ProfileContext);
    if (!context) {
        throw new Error('useProfileContext must be used within a ProfileProvider');
    }
    return context;
};
